import VerticalDivider from 'components/divider/VerticalDivider.tsx';
import InfoTooltip from 'components/InfoTooltip';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import React, { Fragment, useCallback, useMemo } from 'react';
import { t } from 'utils/getTranslations';
import {
    CompositeFilterForm,
    MainFilterForm,
    MainFilterInForm,
    QuickFilterForm,
    SelectOption,
    TextMatch,
} from '../ListTableTypes';
import { getSelectorKey } from '../getSelectorKey.ts';
import { changeCheckBoxGroupFilterState, CheckboxGroup } from './inputs/CheckboxGroup.tsx';
import ContactDynamicMultiselect from './inputs/ContactDynamicMultiSelect.tsx';
import { DateRange, OffsetDateTimeRange } from './inputs/DateRange.tsx';
import { DateSelector } from './inputs/DateSelector.tsx';
import { Dropdown } from './inputs/Dropdown.tsx';
import { DynamicDropdown } from './inputs/DynamicDropdown.tsx';
import { Composite } from './inputs/Composite.tsx';
import { DynamicMultiselect } from './inputs/DynamicMultiSelect.tsx';
import { Interval } from './inputs/Interval.tsx';
import { Multiselect } from './inputs/Multiselect.tsx';
import { NumberField } from './inputs/NumberField.tsx';
import { Text, TextEmptiness } from './inputs/Text.tsx';

type QuickFilterChangeHandler = (value: boolean, groupSelector: string, key: string, immediateFetch?: boolean) => void;

type Props = {
    filterState: MainFilterForm;
    handleChange: (state: MainFilterForm) => void;
    quickFilters: QuickFilterForm;
    handleQuickFilterChange: QuickFilterChangeHandler;
    hideDividers?: boolean;
    /** A tooltip after a group's header, by the group's message key. */
    groupTooltips?: Record<string, string>;
};

type ControlProps = Omit<Props, 'hideDividers' | 'groupTooltips'> & {
    filter: MainFilterInForm;
    /** The filters a dynamic picker's `getFilter` reads its siblings from. */
    mainFiltersInForm: MainFilterInForm[];
};

/**
 * Writes into the filter in place and reports whether it exists. In place on purpose: the drawer hands
 * the same state object on, and a control that reports twice in one event (a date range's quick pick
 * sets both ends) relies on the second write seeing the first.
 */
const writeFilter = (
    filterState: MainFilterForm,
    groupSelector: string,
    write: (filter: MainFilterInForm) => void
): boolean => {
    const filter = filterState?.[groupSelector];
    if (!filter) return false;
    write(filter);
    return true;
};

/**
 * One filter's control as the drawer renders it. Its handlers write the new value into
 * `filterState` and hand that over to `handleChange`; the filter's own `onChange` then runs with the
 * quick filters, as in the drawer.
 */
export const MainFilterControl: React.FC<ControlProps> = ({
    filter: fil,
    filterState,
    handleChange,
    quickFilters,
    handleQuickFilterChange,
    mainFiltersInForm,
}) => {
    const handleIntervalChange = useCallback(
        (value: number | string | null, groupSelector: string, setMin: boolean) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'interval') {
                    if (setMin) {
                        filter.from = value as number;
                    } else {
                        filter.to = value as number;
                    }
                } else if (
                    (filter.type === 'daterange' || filter.type === 'offset-date-time-range') &&
                    (value === null || typeof value === 'string')
                ) {
                    if (setMin) {
                        filter.from = value;
                    } else {
                        filter.to = value;
                    }
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleNumberChange = useCallback(
        (value: number | null, groupSelector: string) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'number') {
                    filter.value = value;
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleCheckboxToggle = useCallback(
        (value: boolean, groupSelector: string, key: string) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'checkboxgroup') {
                    changeCheckBoxGroupFilterState(key, value, filter);
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleMultiSelect = useCallback(
        (values: SelectOption[], groupSelector: string) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'multiselect' || filter.type === 'dynamicmultiselect') {
                    filter.selected = values;
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleExcludingChange = useCallback(
        (isExcluding: boolean, groupSelector: string) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'multiselect' || filter.type === 'dynamicmultiselect') {
                    filter.isExcluding = isExcluding;
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleDropdownSelect = useCallback(
        (value: SelectOption | null, groupSelector: string) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'dropdown' || filter.type === 'dynamicdropdown') {
                    filter.selected = value;
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleMatchChange = useCallback(
        (match: TextMatch | TextEmptiness, groupSelector: string) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'text') {
                    // Emptiness clears the terms, as picking it in the new design's switch does.
                    filter.isEmpty = match === 'empty';
                    filter.isNotEmpty = match === 'not-empty';
                    if (match === 'empty' || match === 'not-empty') filter.value = null;
                    else filter.match = match;
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleValueChange = useCallback(
        (value: string | string[] | null, groupSelector: string) => {
            const written = writeFilter(filterState, groupSelector, (filter) => {
                if (filter.type === 'text') {
                    filter.value = value;
                    // A typed term ends the emptiness the Match select picked, as a value ends a list's pinned pair.
                    if (value?.length && filter.emptyComparator) {
                        filter.isEmpty = false;
                        filter.isNotEmpty = false;
                    }
                }
                if (filter.type === 'date' || filter.type === 'offset-date-time') {
                    filter.value = value as string | null;
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    const handleCompositeChange = useCallback(
        (composite: CompositeFilterForm) => {
            const written = writeFilter(filterState, getSelectorKey(composite), (filter) => {
                if (filter.type === 'composite') {
                    filter.parts = composite.parts;
                    filter.isExcluding = composite.isExcluding;
                }
            });
            if (written) handleChange(filterState);
        },
        [filterState, handleChange]
    );

    switch (fil.type) {
        case 'composite':
            return (
                <Composite
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={handleCompositeChange}
                />
            );
        case 'date':
        case 'offset-date-time':
            return (
                <DateSelector
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={handleValueChange}
                />
            );
        case 'daterange':
            return (
                <DateRange
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={handleIntervalChange}
                />
            );
        case 'offset-date-time-range':
            return (
                <OffsetDateTimeRange
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={handleIntervalChange}
                />
            );
        case 'checkboxgroup':
            return (
                <CheckboxGroup
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    direction={fil.displayDirection}
                    onChange={(val, argument, groupSelector, key) => {
                        if (fil.multiple !== true && val) {
                            fil.filters
                                .filter(({ isSelected }) => isSelected)
                                .forEach((filter) => handleCheckboxToggle(!filter.value, groupSelector, filter.key));
                        }
                        handleCheckboxToggle(val, groupSelector, key);
                        if (fil.onChange) {
                            const value = { label: argument, value: val } as SelectOption;
                            fil.onChange(value, groupSelector, quickFilters, handleQuickFilterChange);
                        }
                    }}
                />
            );
        case 'interval':
            return <Interval filter={fil} onChange={handleIntervalChange} />;
        case 'number':
            return (
                <NumberField
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={handleNumberChange}
                />
            );
        case 'text':
            return (
                <Text
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={handleValueChange}
                    onMatchChange={handleMatchChange}
                />
            );
        case 'multiselect':
            return (
                <Multiselect
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={(val, groupSelector) => {
                        handleMultiSelect(val, groupSelector);
                        if (fil.onChange) {
                            fil.onChange(val, groupSelector, quickFilters, handleQuickFilterChange);
                        }
                    }}
                    onExcludingChange={handleExcludingChange}
                />
            );
        case 'dynamicmultiselect': {
            // Domain-specific variants are opted in via `subType`. `'contact'` swaps to the
            // Contact-aware renderer that auto-wires party number + type icon and the
            // Persona non grata chip.
            const FilterPieceComponent = fil.subType === 'contact' ? ContactDynamicMultiselect : DynamicMultiselect;
            return (
                <FilterPieceComponent
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    mainFiltersInForm={mainFiltersInForm}
                    onChange={(val, groupSelector) => {
                        handleMultiSelect(val, groupSelector);
                        if (fil.onChange) {
                            fil.onChange(val, groupSelector, quickFilters, handleQuickFilterChange);
                        }
                    }}
                    onExcludingChange={handleExcludingChange}
                />
            );
        }
        case 'dropdown':
            return (
                <Dropdown
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    onChange={(val, groupSelector) => {
                        handleDropdownSelect(val, groupSelector);
                        if (fil.onChange) {
                            fil.onChange(val, groupSelector, quickFilters, handleQuickFilterChange);
                        }
                    }}
                />
            );
        case 'dynamicdropdown':
            return (
                <DynamicDropdown
                    filter={fil}
                    quickFilters={quickFilters}
                    mainFilters={filterState}
                    mainFiltersInForm={mainFiltersInForm}
                    onChange={(val, groupSelector) => {
                        handleDropdownSelect(val, groupSelector);
                        if (fil.onChange) {
                            fil.onChange(val, groupSelector, quickFilters, handleQuickFilterChange);
                        }
                    }}
                />
            );
        default:
            return null;
    }
};

const MainFilters: React.FC<Props> = ({
    filterState,
    handleChange,
    quickFilters,
    handleQuickFilterChange,
    hideDividers,
    groupTooltips,
}) => {
    const getMainFilterComponentList = (filters: MainFilterInForm[]) =>
        filters.map((fil, index) => (
            <Fragment key={getSelectorKey(fil)}>
                {fil.type === 'checkboxgroup' && index !== 0 && !hideDividers && <VerticalDivider />}
                <MainFilterControl
                    filter={fil}
                    filterState={filterState}
                    handleChange={handleChange}
                    quickFilters={quickFilters}
                    handleQuickFilterChange={handleQuickFilterChange}
                    mainFiltersInForm={filters}
                />
            </Fragment>
        ));

    const filtersByCategory = useMemo(() => {
        const groups = {} as Record<string, Record<string, MainFilterInForm[]>>;
        const other = {} as Record<string, MainFilterInForm[]>;
        if (!filterState) return groups;
        let subgroupNumber = 0;
        Object.values(filterState).forEach((filter) => {
            if (filter.group) {
                const groupValues = groups[filter.group] ?? {};

                if (filter.subgroup) {
                    const subgroupValues = groupValues[filter.subgroup] ?? [];
                    subgroupValues.push(filter);
                    groupValues[filter.subgroup] = subgroupValues;
                } else {
                    const subgroupValues = groupValues['noSubgroup' + subgroupNumber] ?? [];
                    subgroupValues.push(filter);
                    groupValues['noSubgroup' + subgroupNumber] = subgroupValues;
                    subgroupNumber++;
                }

                groups[filter.group] = groupValues;
            } else {
                const others = other?.noSubgroup ?? [];
                others.push(filter);
                other['noSubgroup'] = others;
            }
        });
        return { other, ...groups };
    }, [filterState]);

    return (
        // `data-form-body`: a filter's dropdown opens inside this panel, so it scrolls with the panel
        // and is clipped by it rather than floating over the drawer's header.
        <div data-form-body className="bg-grey-50 flex flex-col gap-4 overflow-auto p-6">
            {filterState
                ? Object.keys(filtersByCategory).map((group) => {
                      if (!Object.keys(filtersByCategory[group]).length) {
                          return null;
                      }
                      if (group !== 'other') {
                          return (
                              // One accordion per group, each open to start with and each opened and
                              // closed on its own.
                              <Accordion
                                  key={`filter-acc-${group}`}
                                  defaultValue={[group]}
                                  className="bg-paper border-divider rounded-lg border px-4 pb-4 shadow-xs"
                              >
                                  {/* A settled open group does not clip: a filter that keeps its list inside
                                      its own box (DynamicMultiselect) opens over the groups below it, and the
                                      scrolling panel around them clips it instead. While the kit panel's
                                      height transitions it keeps clipping — an open waits it out with a
                                      delayed discrete overflow transition, a close clips at once. */}
                                  <AccordionItem
                                      value={group}
                                      className="not-last:border-b-0 *:data-[slot=accordion-content]:data-open:not-data-starting-style:overflow-visible motion-safe:*:data-[slot=accordion-content]:data-open:not-data-starting-style:[transition:height_250ms_ease-in-out,overflow_0s_250ms_allow-discrete]"
                                  >
                                      <AccordionTrigger className="border-divider min-h-[46px] items-center rounded-none border-0 border-b py-2.5 font-semibold hover:no-underline">
                                          {groupTooltips?.[group] ? (
                                              <span className="flex items-center gap-1">
                                                  {t(group)}
                                                  {/* Inside the trigger's button: a click on the "i" must not fold the group. */}
                                                  <span
                                                      role="presentation"
                                                      onClick={(event) => event.stopPropagation()}
                                                  >
                                                      <InfoTooltip text={groupTooltips[group]} nested />
                                                  </span>
                                              </span>
                                          ) : (
                                              t(group)
                                          )}
                                      </AccordionTrigger>
                                      <AccordionContent className="flex flex-col gap-4 pt-3 pb-0">
                                          {Object.keys(filtersByCategory[group]).map((subgroup) => {
                                              if (subgroup.startsWith('noSubgroup')) {
                                                  return getMainFilterComponentList(filtersByCategory[group][subgroup]);
                                              } else {
                                                  return (
                                                      <div
                                                          key={`filter-acc-${group}-${subgroup}`}
                                                          className="flex flex-row items-end"
                                                      >
                                                          {getMainFilterComponentList(
                                                              filtersByCategory[group][subgroup]
                                                          )}
                                                      </div>
                                                  );
                                              }
                                          })}
                                      </AccordionContent>
                                  </AccordionItem>
                              </Accordion>
                          );
                      }

                      return (
                          <div key={`filter-acc-${group}`} className="flex flex-col gap-4">
                              {getMainFilterComponentList(filtersByCategory[group]['noSubgroup'])}
                          </div>
                      );
                  })
                : null}
        </div>
    );
};

export default MainFilters;
